import type { LocaleInfo } from '../api/types'

/** Second-person pronoun per language, so `de-sie` / `de-du` never render as
 *  plain "German". Falls back to the generic formal/informal wording. */
const PRONOUNS: Record<string, { formal: string; informal: string }> = {
  de: { formal: 'Sie', informal: 'du' },
}

const languageName = (() => {
  const display = new Intl.DisplayNames(['en'], { type: 'language' })
  return (code: string) => display.of(code) ?? code.toUpperCase()
})()

export interface LocaleLabel {
  code: string
  /** Column header, e.g. "DE · Sie" */
  short: string
  /** Long form, e.g. "German — formal (Sie)" */
  full: string
  /** One-word variant marker, e.g. "Sie" */
  variant: string | null
  isSource: boolean
  formality: LocaleInfo['formality']
}

export function localeLabel(locale: LocaleInfo): LocaleLabel {
  const [lang, ...rest] = locale.code.split('-')
  const suffix = rest.join('-')
  const pronouns = PRONOUNS[lang]
  const variant =
    locale.formality && pronouns
      ? pronouns[locale.formality]
      : suffix
        ? suffix.toUpperCase()
        : null
  const shown = variant ? variant.charAt(0).toUpperCase() + variant.slice(1) : null
  const formality = locale.formality ? `${locale.formality}` : null
  return {
    code: locale.code,
    short: shown ? `${lang.toUpperCase()} · ${shown}` : lang.toUpperCase(),
    full: locale.is_source
      ? `${languageName(lang)} (source)`
      : [languageName(lang), formality && shown ? `${formality} (${shown})` : formality]
          .filter(Boolean)
          .join(' — '),
    variant: shown,
    isSource: locale.is_source,
    formality: locale.formality,
  }
}

export function labelMap(locales: LocaleInfo[]): Record<string, LocaleLabel> {
  return Object.fromEntries(locales.map((locale) => [locale.code, localeLabel(locale)]))
}
